import { PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING as ShortcutKeys } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { areEq } from 'shared/utils/localStrorageUtils'

const {
    InsertPill,
    InsertUOM,
    InsertText,
    AddSpecifierNote,
    SelectAll,
    Copy,
    Save,
    Close,
    Undo,
    Tab,
    Promote,
    Demote,
    Escape,
    Backspace,
    Delete,
    ArrowRight,
    ArrowLeft,
    ArrowUp,
    ArrowDown,
    MoveUp,
    MoveDown,
    Export,
    ExportPDF,
    ExportWord,
    Preview,
    Preview2,
    SupportingDocs,
    Evaluations,
    CopyPT,
    Enter,
    Space,
} = ShortcutKeys

/**
 * Returns the shortcut key for a given event and key.
 *
 * @param {keyof typeof ShortcutKeys} name - The key to check.
 * @param {React.KeyboardEvent<HTMLDivElement> | KeyboardEvent} event - The keyboard event to check.
 *
 * @returns {typeof ShortcutKeys[keyof typeof ShortcutKeys] | ''} The mapped shortcut value if it exists, empty string otherwise.
 *
 * @example
 * // Example usage of tryGetShortcutKey
 * tryGetShortcutKey('InsertUOM', event) // returns value of SHORTCUT_KEYS['InsertUOM'] if event.ctrlKey and event.shiftKey are true and event.key is '>'
 */
export const tryGetShortcutKey = (
    name: keyof typeof ShortcutKeys,
    event: React.KeyboardEvent<Element> | KeyboardEvent,
    isNonEditorEvent?: boolean
): typeof ShortcutKeys[keyof typeof ShortcutKeys] | '' => {
    const {
        key: inputKey,
        ctrlKey: Ctrl,
        altKey: Alt,
        shiftKey: Shift,
        metaKey: Meta,
    } = event
    const NotFound = ''

    // utility func to get shortcut key by comparing expected and mapped value.
    const tryGet = (expectedShortcutValue: string) =>
        areEq(expectedShortcutValue, ShortcutKeys[name])
            ? ShortcutKeys[name]
            : NotFound
    const isShiftKey = Shift && !Alt && !Ctrl
    const isDemote = !Ctrl && !Shift && !Alt && areEq(inputKey, Demote)
    const isDirectKeyMatch =
        areEq(inputKey, name) || (areEq(inputKey, ' ') && isNonEditorEvent)

    // Ctrl + Alt + key
    if (Ctrl || Meta) {
        // Ctrl + Alt + key
        if (Alt && !Shift) {
            switch (inputKey.toUpperCase()) {
                case 'C':
                    return tryGet(CopyPT)
                case 'I':
                    return tryGet(Preview)
                case 'S':
                    return tryGet(AddSpecifierNote)
                case 'P':
                    return tryGet(InsertPill)
                case 'T':
                    return tryGet(InsertText)
                case 'D':
                    return tryGet(ExportPDF)
                case 'W':
                    return tryGet(ExportWord)
                case 'R':
                    return tryGet(Preview2)
                case 'E':
                    return tryGet(Evaluations)
                default:
                    break
            }
        }
        // Ctrl + Shift + key
        if (Shift && !Alt) {
            switch (inputKey.toUpperCase()) {
                case '>':
                case '.': // use . (dot) to support for MacOS
                    console.warn(
                        `Ctrl+Shift+> is be deprecated. Use ${tryGet(
                            InsertUOM
                        )} to insert uom instead.`
                    )
                    return NotFound
                case '<':
                case ',': // use , (comma) to support for MacOS
                    console.warn(
                        `Ctrl+Shift+< is be deprecated. Use ${tryGet(
                            InsertText
                        )} to insert text instead.`
                    )
                    return NotFound
                case 'E':
                    return tryGet(Export)
                default:
                    break
            }
        }
        // Ctrl + key
        if (!Alt && !Shift) {
            switch (inputKey.toUpperCase()) {
                case '[':
                    console.warn(
                        `Ctrl+[ will be deprecated. Use ${tryGet(
                            InsertPill
                        )} to insert pill instead.`
                    )
                    return NotFound
                case 'A':
                    return tryGet(SelectAll)
                case 'C':
                    return tryGet(Copy)
                case 'I':
                    return tryGet(SupportingDocs)
                case 'S':
                case 's':
                    return tryGet(Save)
                case 'U':
                    return tryGet(InsertUOM)
                case 'X':
                    return tryGet(Close)
                case 'Z':
                    return tryGet(Undo)
                default:
                    break
            }
        }
    }
    // Alt + Shift + key
    else if (Alt) {
        if (Shift) {
            switch (inputKey.toUpperCase()) {
                case ArrowUp.toUpperCase():
                    return tryGet(MoveUp)
                case ArrowDown.toUpperCase():
                    return tryGet(MoveDown)
                default:
                    break
            }
        }
    }
    // Shift + key
    else if (isShiftKey) {
        if (areEq(inputKey, Tab)) {
            return tryGet(Promote)
        }
    } else if (isDemote) {
        return tryGet(Demote)
    }
    // other key mapping
    else if (isDirectKeyMatch) {
        return tryGet(ShortcutKeys[name])
    }

    return NotFound
}

/**
 * Prevents default behavior and stops propagation for keyboard events.
 * Commonly used to prevent default Tab/Shift+Tab behavior during promote/demote operations.
 * 
 * @param {React.KeyboardEvent<Element> | KeyboardEvent} event - The keyboard event to prevent
 */
export const preventEventDefault = (
    event: React.KeyboardEvent<Element> | KeyboardEvent
): void => {
    event.preventDefault()
    event.stopPropagation()
}
